import { Input } from "@9amhealth/shared";
import type { FC } from "react";
import type { FactoryArg, InputMask } from "imask";
import React, { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { validatePhoneNumber } from "src/lib/formatPhoneNumber";
import translate from "src/lib/translate";
import type { TranslationKey } from "src/types/translationKey";
import { FormErrorContext } from "../QuestionnaireStep/FormErrorContext";

interface PhoneNumberInputProps {
  name: string;
  label?: TranslationKey;
  required?: boolean;
  onNumberChange?: (value: string) => void;
}

const PhoneNumberInput: FC<PhoneNumberInputProps> = ({
  name,
  label,
  required,
  onNumberChange
}) => {
  const { setValue, getValues } = useFormContext();
  const { setFieldError, clearFieldError } = React.useContext(FormErrorContext);
  const [error, setError] = useState("");

  const existing = getValues()[name] as string | undefined;
  const defaultValue = useMemo(() => existing ?? "", [existing]);

  const handleMaskChange = (maskInstance: InputMask<FactoryArg>) => {
    clearFieldError(name);
    setError("");

    const { value } = maskInstance;
    const { isComplete } = maskInstance.masked;
    const isValid = validatePhoneNumber(value, "US");

    if (isComplete && isValid) {
      setValue(name, value);
      onNumberChange?.(value);
    } else {
      setValue(name, "");
      if (isComplete && !isValid) {
        const errorMsg = translate("error_phone_number_invalid");
        setError(errorMsg);
        setFieldError(name, errorMsg);
      }
      onNumberChange?.("");
    }
  };

  useEffect(() => {
    if (defaultValue) {
      const isValid = validatePhoneNumber(defaultValue, "US");
      if (isValid) {
        setValue(name, defaultValue);
        onNumberChange?.(defaultValue);
      }
    }
  }, [defaultValue]);

  return (
    <Input
      name="phone-number"
      defaultValue={defaultValue}
      label={label ? translate(label) : ""}
      isRequired={required}
      inputMode="tel"
      mask={{ mask: "(000) 000-0000", lazy: false }}
      error={error}
      onMaskChange={handleMaskChange}
    />
  );
};

export default PhoneNumberInput;
